import { AdminWorkspaceRoles, Roles } from '~/data/user/role';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import { WorkspacesSettings } from '~/scenes/MyWorkspace/hooks';
import { RoleGroups, getRoleGroups } from '~/services/Permissions';
import { NonEmptyArray } from '~/types/common';

import {
  MyWorkspaceWidgetNames,
  getAvailableWidgets,
  getRoleWidgets,
  widgetsAllowedRoles,
} from './components/Widgets/data/widgets';
import { myWorkspaceWidgetsMap } from './components/Widgets/data/widgetsMap';

export const MyWorkspaceUserSettingsKey = 'MY_WORKSPACE';
export const CompanyWorkspaceSettingsKey = 'COMPANY_WORKSPACE';
export const WorkspaceId_NO_WORKSPACE = 'NO_WORKSPACE';

export interface WidgetSize {
  w: number;
  h: number;
}

export interface WidgetSettings {
  id: string;
  widget: MyWorkspaceWidgetNames;
  size?: WidgetSize;
  title?: string;
  isVisible?: boolean;
  config?: object;
}

export interface WorkspaceSettings {
  id: string;
  name: string;
  widgets: WidgetSettings[];
  type: WorkspaceType | string;
  restrictToRoles: AdminWorkspaceRoles[];
}

export interface UserSettings {
  workspaces: Array<WorkspaceSettings>;
  activeWorkspaceId?: string;
}

export const getRolesDefaultWidgets = (role: Roles): WidgetSettings[] => {
  return getRoleWidgets(role).map((widgetName) => {
    return {
      id: widgetName,
      widget: widgetName,
      isVisible: true,
    };
  });
};

export const getRoleGroupDefaultWidgets = (
  roleGroup: RoleGroups,
): WidgetSettings[] => {
  return (Object.keys(widgetsAllowedRoles) as MyWorkspaceWidgetNames[])
    .filter((widgetName) => {
      return widgetsAllowedRoles[widgetName].includes(roleGroup);
    })
    .map((widgetName) => {
      return {
        id: widgetName,
        widget: widgetName,
        isVisible: true,
        config:
          myWorkspaceWidgetsMap[widgetName].config?.defaultData?.[roleGroup],
      };
    });
};

export const defaultUserSettings = (roles: string[]) => {
  const availableRoleGroups = getRoleGroups(roles);

  return [
    {
      roleGroup: RoleGroups.STRAIGHT_MANAGER,
      workspaceName: 'Manager',
    },
    {
      roleGroup: RoleGroups.RESPONSIBLE_MANAGER,
      workspaceName: 'Responsible Manager',
    },
    {
      roleGroup: RoleGroups.HR,
      workspaceName: 'HR',
    },
  ].reduce(
    (userSettings, { roleGroup, workspaceName }) => {
      if (!availableRoleGroups.includes(roleGroup)) {
        return userSettings;
      }

      return {
        workspaces: [
          ...userSettings.workspaces,
          {
            id: roleGroup,
            name: workspaceName,
            widgets: getRoleGroupDefaultWidgets(roleGroup),
            type: WorkspaceType.Default,
          },
        ],
        activeWorkspaceId: roleGroup,
      } as UserSettings;
    },
    {
      workspaces: [
        {
          id: RoleGroups.EMPLOYEE,
          name: 'Employee',
          widgets: getRoleGroupDefaultWidgets(RoleGroups.EMPLOYEE),
          type: WorkspaceType.Default,
        },
      ],
      activeWorkspaceId: RoleGroups.EMPLOYEE,
    } as UserSettings,
  );
};

export const getMyWorkspaceUserSettingsResolver = (
  roles: string[],
  enabledModules: string[],
) => {
  const availableWidgets = getAvailableWidgets(roles, enabledModules);

  const noWorkspaces: WorkspacesSettings = {
    workspaces: [],
  };

  return (remoteUserSettings?: Partial<UserSettings>): UserSettings => {
    try {
      if (!remoteUserSettings?.workspaces?.length) {
        return noWorkspaces;
      }

      return {
        workspaces: remoteUserSettings.workspaces.map((workspace) => ({
          ...workspace,
          widgets: workspace.widgets.filter(({ widget }) => {
            return availableWidgets.includes(widget);
          }),
          type: workspace.type || WorkspaceType.Personal, // Temp fix until BE ready
        })) as NonEmptyArray<WorkspaceSettings>,
        activeWorkspaceId: remoteUserSettings.activeWorkspaceId,
      };
    } catch {
      return noWorkspaces;
    }
  };
};
